/**
 * TotpQuickViewCard.tsx
 *
 * Compact TOTP entry card for the Quick Access overlay.
 * Shows masked code with countdown timer and copy button.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { Copy, Check, Timer } from 'lucide-react';
import { cn } from '@/base/lib/utils';
import { useToastStore } from '@/base/stores/toastStore';
import type { TotpEntry, TotpCode } from '@/base/hooks/useTotpEntries';

interface TotpQuickViewCardProps {
  entry: TotpEntry;
  code: TotpCode | undefined;
}

export function TotpQuickViewCard({ entry, code }: TotpQuickViewCardProps) {
  const [isCopied, setIsCopied] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const toast = useToastStore();
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Format account label (truncate if too long)
  const formatLabel = (label: string) => {
    if (label.length > 20) {
      return label.substring(0, 17) + '...';
    }
    return label;
  };

  // Get issuer initial for icon
  const issuerInitial = entry.issuer.charAt(0).toUpperCase();

  // Generate color from issuer name
  const getIssuerColor = (issuer: string) => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-yellow-500',
      'bg-red-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-teal-500',
    ];
    const hash = issuer.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  // Copy code to clipboard
  const handleCopy = useCallback(async () => {
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code.code);
      setIsCopied(true);
      toast.success('Code copied to clipboard');

      // Auto-clear clipboard after 30 seconds
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current);
      }
      clearTimerRef.current = setTimeout(async () => {
        try {
          await navigator.clipboard.writeText('');
        } catch (error) {
          console.error('Failed to clear clipboard:', error);
        }
      }, 30000);

      // Reset copied state after 2 seconds
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      toast.error('Failed to copy code');
    }
  }, [code, toast]);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (clearTimerRef.current) {
        clearTimeout(clearTimerRef.current);
      }
    };
  }, []);

  // Calculate progress percentage
  const progressPercentage = code ? (code.remainingSeconds / entry.period) * 100 : 0;
  const isExpiring = code && code.remainingSeconds <= 5;

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg border',
        'bg-[var(--grok-surface-1)] border-[var(--grok-border)]',
        'hover:bg-[var(--grok-surface-2)] transition-colors',
        isExpiring && 'border-[var(--ems-danger)]'
      )}
      onMouseEnter={() => setRevealed(true)}
      onMouseLeave={() => setRevealed(false)}
    >
      {/* Issuer Icon */}
      <div
        className={cn(
          'w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-lg',
          getIssuerColor(entry.issuer)
        )}
      >
        {issuerInitial}
      </div>

      {/* Entry Info */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[var(--grok-text-primary)] truncate">
          {entry.issuer}
        </div>
        <div className="text-xs text-[var(--grok-text-muted)] truncate">
          {formatLabel(entry.accountLabel)}
        </div>
      </div>

      {/* Code Display */}
      <div className="flex items-center gap-2">
        {code ? (
          <>
            <div
              className={cn(
                'font-mono text-sm font-semibold',
                revealed ? 'text-[var(--grok-text-primary)]' : 'text-[var(--grok-text-muted)]'
              )}
            >
              {revealed ? code.code : '●●●●●●'}
            </div>

            {/* Countdown Timer */}
            <div className="relative w-8 h-8 flex items-center justify-center">
              <svg className="w-8 h-8 transform -rotate-90">
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  stroke="var(--grok-border)"
                  strokeWidth="2"
                  fill="none"
                />
                <circle
                  cx="16"
                  cy="16"
                  r="14"
                  stroke={isExpiring ? 'var(--ems-danger)' : 'var(--ems-primary)'}
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 14}`}
                  strokeDashoffset={`${2 * Math.PI * 14 * (1 - progressPercentage / 100)}`}
                  className="transition-all duration-1000 ease-linear"
                />
              </svg>
              <span
                className={cn(
                  'absolute text-xs font-semibold',
                  isExpiring ? 'text-[var(--ems-danger)]' : 'text-[var(--grok-text-muted)]'
                )}
              >
                {code.remainingSeconds}
              </span>
            </div>

            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className={cn(
                'p-2 rounded-md transition-colors',
                isCopied
                  ? 'bg-[var(--ems-success)] text-white'
                  : 'text-[var(--grok-text-muted)] hover:text-[var(--grok-text-primary)] hover:bg-[var(--grok-surface-2)]'
              )}
              aria-label={isCopied ? 'Copied' : 'Copy code'}
              disabled={isCopied}
            >
              {isCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>
          </>
        ) : (
          <div className="text-xs text-[var(--grok-text-muted)] flex items-center gap-1">
            <Timer className="w-3 h-3" />
            Loading...
          </div>
        )}
      </div>
    </div>
  );
}
